import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlaskConical, Play, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { api, PRESET_LABELS, authedUrl } from "@/lib/api";
import { EnginePicker } from "@/components/benchmark/EnginePicker";
import { confirmPaidRun } from "@/components/engines/paid";
import { fmtDate } from "@/lib/format";
import { BenchmarkTable } from "@/components/benchmark/BenchmarkTable";
import { SideBySide } from "@/components/benchmark/SideBySide";
import { BenchmarkRatings } from "@/components/benchmark/BenchmarkRatings";
import { ImportStems } from "@/components/benchmark/ImportStems";

const Disclaimer = () => (
  <div data-testid="benchmark-disclaimer" className="text-xs text-slate-400 border border-[#253044] rounded-sm px-3 py-2 bg-[#0B0E14]">
    Benchmarks measure differences objectively (time, cost, model, output, listening). No open-source or in-house result is claimed equivalent to Gaudio or AudioShake.
  </div>
);

const NewBenchmark = ({ onCreated }) => {
  const [media, setMedia] = useState([]);
  const [engines, setEngines] = useState([]);
  const [mediaId, setMediaId] = useState("");
  const [preset, setPreset] = useState("DME");
  const [picked, setPicked] = useState([]);
  const [options, setOptions] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/media").then((r) => { setMedia(r.data); setMediaId((m) => m || r.data[0]?.id || ""); });
    api.get("/engines").then((r) => setEngines(r.data.engines));
  }, []);
  const run = async () => {
    if (!confirmPaidRun(engines, picked)) return;
    setBusy(true);
    try {
      const r = await api.post("/benchmark", { media_id: mediaId, engines: picked, preset, engine_options: options });
      toast.success(picked.length ? `Benchmark started across ${picked.length} engine(s)` : "Empty benchmark created — import stems or queue engines");
      onCreated(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="panel p-4 space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <label className="block">
          <span className="micro">Source master</span>
          <select data-testid="benchmark-media-select" value={mediaId} onChange={(e) => setMediaId(e.target.value)} className="mt-1 w-full bg-[#0B0E14] border border-[#253044] rounded-sm px-3 py-2 text-sm">
            {media.map((m) => <option key={m.id} value={m.id}>{m.source_filename}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="micro">Preset</span>
          <select data-testid="benchmark-preset-select" value={preset} onChange={(e) => setPreset(e.target.value)} className="mt-1 w-full bg-[#0B0E14] border border-[#253044] rounded-sm px-3 py-2 text-sm">
            {Object.entries(PRESET_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
      </div>
      <div>
        <div className="micro mb-2">Engines to compare · nothing is pre-selected; paid engines ask for confirmation</div>
        <EnginePicker engines={engines} picked={picked} setPicked={setPicked} options={options} setOptions={setOptions} />
      </div>
      <button data-testid="benchmark-run-all-button" disabled={busy || !mediaId} onClick={run}
        className="flex items-center gap-2 px-4 h-10 rounded-sm bg-[#E11D48] hover:bg-[#be123c] text-white text-sm font-semibold disabled:opacity-40">
        <Play size={15} /> {picked.length ? `Run benchmark on ${picked.length} engine(s)` : "Create empty benchmark (import stems later)"}
      </button>
    </div>
  );
};

function BenchmarkDetail({ id }) {
  const [bm, setBm] = useState(null);
  const nav = useNavigate();
  const load = useCallback(() => api.get(`/benchmark/${id}`).then((r) => setBm(r.data)).catch(() => nav("/benchmark")), [id, nav]);
  useEffect(() => {
    load();
    const t = setInterval(() => { load(); }, 3000);
    return () => clearInterval(t);
  }, [load]);
  if (!bm) return <div className="text-slate-500 text-sm p-8">Loading benchmark…</div>;
  const active = bm.jobs.some((j) => ["queued", "processing"].includes(j.status));
  return (
    <div data-testid="benchmark-detail" className="space-y-4 fade-up">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button data-testid="benchmark-back-button" onClick={() => nav("/benchmark")} className="text-xs text-slate-400 hover:text-white">← All benchmarks</button>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-1">{bm.name}</h1>
          <p className="text-sm text-slate-400 mt-1">Preset {bm.preset} · {bm.engines.length} engines · {fmtDate(bm.created_at)}{active ? " · running…" : ""}</p>
        </div>
        {bm.jobs.some((j) => j.status === "completed") && (
          <a data-testid="benchmark-download-all-fcp" href={authedUrl(`/benchmark/${bm.id}/download-all`)}
            className="flex items-center gap-2 px-3 h-9 rounded-sm border border-[#3A4A68] hover:bg-[#1E2738] text-xs font-medium whitespace-nowrap"><Download size={14} /> All runs · WAV for FCP</a>
        )}
      </div>
      <Disclaimer />
      <BenchmarkTable jobs={bm.jobs} onChanged={load} />
      <ImportStems benchmark={bm} onDone={load} />
      <SideBySide key={bm.jobs.filter((j) => j.status === "completed").length} jobs={bm.jobs} original={bm.original_file} />
      <BenchmarkRatings key={`r-${bm.jobs.filter((j) => j.status === "completed").length}`} jobs={bm.jobs} onSaved={load} />
    </div>
  );
}

export default function BenchmarkLab() {
  const { benchmarkId } = useParams();
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get("/benchmark").then((r) => setList(r.data)), []);
  useEffect(() => { if (!benchmarkId) load(); }, [benchmarkId, load]);
  if (benchmarkId) return <BenchmarkDetail id={benchmarkId} />;
  const remove = async (b) => { if (window.confirm("Delete benchmark record? Jobs are kept.")) { await api.delete(`/benchmark/${b.id}`); load(); } };
  return (
    <div data-testid="benchmark-lab-screen" className="space-y-4 fade-up">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><FlaskConical size={22} className="text-cyan-400" /> Benchmark Lab</h1>
        <p className="text-sm text-slate-400 mt-1">Run one master through Gaudio · AudioShake · Open Source · Gulmohar and listen side by side.</p>
      </div>
      <Disclaimer />
      <NewBenchmark onCreated={(id) => nav(`/benchmark/${id}`)} />
      <div className="panel divide-y divide-[#1c2433]">
        <div className="px-4 py-2.5 micro">Previous benchmarks</div>
        {list.length === 0 && <div className="p-6 text-center text-slate-500 text-sm">No benchmarks yet.</div>}
        {list.map((b) => (
          <div key={b.id} data-testid={`benchmark-item-${b.id}`} className="px-4 py-3 row-hover flex items-center gap-4">
            <button onClick={() => nav(`/benchmark/${b.id}`)} className="flex-1 text-left">
              <div className="font-medium">{b.name}</div>
              <div className="text-xs text-slate-500 font-mono mt-0.5">{b.preset} · {Object.entries(b.job_statuses || {}).map(([e, s]) => `${e}:${s}`).join("  ")} · {fmtDate(b.created_at)}</div>
            </button>
            <button data-testid={`benchmark-delete-${b.id}`} onClick={() => remove(b)} className="p-1.5 rounded-sm border border-[#253044] hover:bg-rose-500/10 hover:text-rose-400 text-slate-400"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
